//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { ReactNode, useContext, useMemo, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { uniq } from 'lodash';
import styled from '@emotion/styled';
import { Box, Link, Menu, MenuItem, Divider } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { CustomerProfile } from '@/redux/slices/slice_types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { applyFilter } from '@/redux/slices/dataTableSlice';
import { selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import { selectRawCustomerList } from '@/redux/selectors/constantsSelectors';
import { selectAllProfileIdNames, selectAllProfiles } from '@/redux/selectors/profileSelectors';
import { TooltipOverflow } from '@/exports/columnCommonComponents';
import FilterChip from '@/components/_common/Filters/FilterChip';
import FilterSearch from '@/components/_common/Filters/FilterSearch';
import FilterMultiSelect, { AnyAllFilterMatchType } from '@/components/_common/Filters/FilterMultiSelect';

export interface FilterBehavior {
  noAdornment?: boolean;
  defaultOption?: string;
  filterInputType?: string;
}

export interface FilterProps {
  label: string;
  options?: Record<string, string> | ReturnType<typeof createSelector>;
  excludeOptions?: Record<string, string> | ReturnType<typeof createSelector>;
  type: string;
  searchable?: boolean;
  component?: React.FC;
  renderApplied?: (opt: string) => React.ReactNode;
  singleSelection?: boolean;
  id: string;
  field?: string;
  limit?: number;
  role?: ReturnType<typeof createSelector>;
  isQuickFilters: boolean;
  filterBehavior?: FilterBehavior;
  expandedFilters?: string[];
  toggleExpandedFilter?: (filter: string) => void;
  anyAllFilters?: FilterProps[];
  advanceFilters?: Record<string, any>;
  onAdvanceFiltersChange?: (newAdvanceFilters: { type: string; selections: any }) => void;
}

export const FilterSelectorOptions = (props: Omit<FilterProps, 'id'>) => {
  const hasPermissions = props.role ? useAppSelector(props.role) : true;
  const options = useAppSelector(props.options);

  return !!Object.keys(options as { [key: string]: string }).length && hasPermissions ? <Filter {...props} options={options} /> : <></>;
};

export default function Filter({
  label,
  options = {},
  type,
  searchable = false,
  component,
  renderApplied,
  singleSelection = false,
  role,
  isQuickFilters,
  expandedFilters = [],
  toggleExpandedFilter,
  anyAllFilters,
  advanceFilters,
  onAdvanceFiltersChange
}: Omit<FilterProps, 'id'>) {
  const isFiltersV2 = useContext(ConfigContext)?.filtersVersion === 2;
  const hasPermissions = role ? useAppSelector(role) : true;
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectDataTableFilters);
  const profiles = useAppSelector(selectAllProfiles);
  const profilesById = useAppSelector(selectAllProfileIdNames);
  const customers = useAppSelector(selectRawCustomerList);
  const isAdvanceFilter = isFiltersV2 && !isQuickFilters;
  const initSelectedOptions = isAdvanceFilter ? advanceFilters?.[type] || [] : filters[type] || [];
  const selectedOptions = initSelectedOptions?.isContain ? initSelectedOptions.values : initSelectedOptions;
  const CustomComponent = component;
  const [anchorEl, setAnchorEl] = useState(null);
  const [anyAllFilterMatchType, setAnyAllFilterMatchType] = useState<AnyAllFilterMatchType>(initSelectedOptions?.isContain ? 'contains' : 'filter');

  const handleClick = (event: any) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const isSelected = (option: string) => selectedOptions.includes(option);
  const hasSelections = () => selectedOptions.length !== 0;
  const isAnyAllEnabled = (anyAllFilters ?? []).some((filter) => filter.id === type);

  const filteredOptions = useMemo(() => {
    const filtersToUse = isAdvanceFilter ? advanceFilters : filters;
    if (type === 'profile_filter' && !!filtersToUse?.customer_filter?.length) {
      const selectedCustomerNames = filtersToUse.customer_filter.map((selection: string) => customers[selection]);
      const filtered: { [key: string]: string } = {};

      Object.keys(options).forEach((id) => {
        const profile = profiles.find((profile: CustomerProfile) => profile.id === Number(id));

        if (!!profile && selectedCustomerNames.includes(profile.customer_name)) {
          filtered[id] = options[id];
        }
      });

      return filtered;
    }

    return options;
  }, [filters, options, type, customers, profiles, advanceFilters, isAdvanceFilter]);

  const handleAllAnyChange = (value: AnyAllFilterMatchType, type: string) => {
    setAnyAllFilterMatchType(value);

    let newValue;
    if (value === 'contains') {
      newValue = {
        type: type,
        selections: {
          values: [...selectedOptions],
          isContain: true
        }
      };
    } else {
      newValue = { type: type, selections: selectedOptions };
    }

    if (isAdvanceFilter && onAdvanceFiltersChange) {
      onAdvanceFiltersChange(newValue);
    } else {
      dispatch(applyFilter(newValue));
    }
  };

  const onChange = (value: { type: string; selections: null | string | string[] }) => {
    let newValue;
    if (isAnyAllEnabled && anyAllFilterMatchType === 'contains') {
      newValue = {
        ...value,
        selections: {
          values: [...(value.selections as string[])],
          isContain: true
        }
      };
    } else {
      newValue = value;
    }

    const filtersToUse = isAdvanceFilter ? advanceFilters : filters;
    if (type === 'customer_filter' && !!filtersToUse?.profile_filter?.length && value.selections?.length) {
      const selectedCustomers = (value.selections as string[]).map((selection) => options[selection]);
      const filteredSelectedProfiles = filtersToUse.profile_filter.filter((id: string) => {
        const profile = profiles.find((profile: CustomerProfile) => profile.id === Number(id));
        return profile?.customer_name !== undefined && selectedCustomers.includes(profile.customer_name);
      });

      newValue = {
        type: 'profile_filter',
        selections: filteredSelectedProfiles
      };
    }

    if (isAdvanceFilter && onAdvanceFiltersChange) {
      onAdvanceFiltersChange(newValue);
    } else {
      dispatch(applyFilter({ ...newValue, debounceNextDataRequest: true }));
    }
  };

  const handleMenuItemClick = (e: any) => {
    let selections: any[] | null = [];
    if (singleSelection) {
      if (isSelected(e.target.id)) {
        if (type === 'hierarchy_filter') return;
        selections = null;
      } else {
        selections = e.target.id;
      }
      onChange({ type, selections });
      setAnchorEl(null);
    } else {
      const selectedId = e.target.id || e.currentTarget.id;
      if (isSelected(selectedId)) {
        selections = selectedOptions.filter((x: string) => x !== selectedId);
      } else {
        selections = [...selectedOptions, selectedId];
      }
      onChange({ type, selections });
    }
  };

  const getApplied = () => {
    if (!selectedOptions.length) return 'All';
    if (singleSelection) return options[selectedOptions] || selectedOptions;
    let displayOptions = selectedOptions;
    if (renderApplied) {
      displayOptions = selectedOptions.map((option: string) => renderApplied(option));
    }
    if (type === 'profile_filter') {
      return <TooltipOverflow length={1} items={displayOptions?.map((so: number) => profilesById[so] || so)} />;
    }
    return <TooltipOverflow length={1} items={displayOptions?.map((so: string) => options[so] || so)} />;
  };

  const handleClear = (e: any) => {
    e.stopPropagation();
    onChange({ type, selections: singleSelection ? '' : [] });
  };

  const handleSelectAll = (filtered?: Record<string, string> | string[]) => {
    let selections: string[] = Object.keys(filteredOptions);

    if (filtered) {
      selections = [...selectedOptions, ...Object.keys(filtered ?? {})];
    }
    onChange({ type, selections: uniq(selections) });
  };

  const handleClearAll = (filtered?: Record<string, string> | string[]) => {
    const filteredKeys = Object.keys(filtered ?? {});
    if (filteredKeys.length === 0) {
      onChange({ type, selections: [] });
    } else {
      const selections = selectedOptions.filter((value: string) => !filteredKeys.includes(value));
      onChange({ type, selections });
    }
  };

  const renderAllButtons = (filtered?: Record<string, string> | string[]) => {
    // do NOT render if single select OR options is less than 5
    const filteredOptionsLength = Object.keys(filteredOptions).length;
    if (singleSelection || filteredOptionsLength < 5) return;

    const content = [];
    if (selectedOptions.length < filteredOptionsLength && (!filtered || Object.keys(filtered).length > 0)) {
      content.push(
        <SelectAllButton key="select-all" onClick={() => handleSelectAll(filtered)}>
          Select All
        </SelectAllButton>
      );
    }
    if (selectedOptions.length > 0 && (!filtered || Object.keys(filtered).length > 0)) {
      content.push(
        <ClearAllButton key="clear-all" onClick={() => handleClearAll(filtered)}>
          Clear All
        </ClearAllButton>
      );
    }

    if (content.length === 2) {
      content.splice(1, 0, <Divider key="divider" orientation="vertical" flexItem />);
    }

    return <ButtonsContainer>{content}</ButtonsContainer>;
  };

  if (!hasPermissions) return null;

  // display quick filter and applied filter chips if it's NOT filters v2 and if user's preference has it pinned on FiltersBar
  if (!isFiltersV2 || (isFiltersV2 && isQuickFilters)) {
    return (
      <div style={{ marginRight: 10 }}>
        <FilterChip
          data-testid={`${type}-chip`}
          label={
            <>
              {label}: <strong style={{ marginLeft: 5 }}>{getApplied()}</strong>
            </>
          }
          isSelected={hasSelections()}
          onClick={handleClick as any}
          onClear={type === 'hierarchy_filter' ? undefined : (handleClear as any)}
        />

        <Menu
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          variant="menu"
        >
          <div>
            {CustomComponent ? (
              <CustomComponent isQuickFilters={isQuickFilters} />
            ) : searchable ? (
              <FilterSearch
                filterId={type}
                onClose={handleClose}
                options={filteredOptions}
                selectedOptions={selectedOptions}
                handleMenuItemClick={handleMenuItemClick}
                label={label}
                renderAllButtons={renderAllButtons}
              />
            ) : (
              <>
                {renderAllButtons()}
                {Object.entries(filteredOptions).map(([value, key]) => (
                  <MenuItem selected={isSelected(value)} key={value} id={value} onKeyDown={(e) => e.stopPropagation()} onClick={handleMenuItemClick}>
                    {key as ReactNode}
                  </MenuItem>
                ))}
              </>
            )}
          </div>
        </Menu>
      </div>
    );
  }

  return CustomComponent ? (
    <CustomComponent
      isQuickFilters={isQuickFilters}
      expandedFilters={expandedFilters}
      onExpand={() => toggleExpandedFilter?.(type)}
      isAdvanceFilter={isAdvanceFilter}
      advanceFilters={advanceFilters}
      onAdvanceFiltersChange={onAdvanceFiltersChange}
    />
  ) : (
    <FilterMultiSelect
      type={type}
      label={label}
      options={options}
      expandedFilters={expandedFilters}
      selectedOptions={selectedOptions}
      onListItemClick={handleMenuItemClick}
      onSelectAll={handleSelectAll}
      onClear={handleClear}
      onExpand={toggleExpandedFilter!}
      isAnyAllEnabled={isAnyAllEnabled}
      matchType={anyAllFilterMatchType}
      onMatchTypeChange={handleAllAnyChange}
    />
  );
}

const SelectAllButton = styled(Link)`
  color: ${(props) => props.theme.palette.primary.main};
  transition: all 0.3s ease;
  &:hover {
    color: ${(props) => props.theme.palette.primary.light};
  }
`;

const ClearAllButton = styled(Link)`
  color: ${(props) => props.theme.palette.grey[600]};
  transition: all 0.3s ease;
  &:hover {
    color: ${(props) => props.theme.palette.grey[400]};
  }
`;

const ButtonsContainer = styled(Box)`
  display: flex;
  align-items: center;
  column-gap: ${(props) => props.theme.spacing(1)};
  padding: ${(props) => `0 ${props.theme.spacing(2)} ${props.theme.spacing(1)}`};
`;
